import { useCallback, useState } from 'react';
import { useAuthStore } from '@/state/store/auth';
import { useGuestSessionStore } from '@/state/store/guestSession';
import { useGuestPaymentStatus } from '@/modules/payment/useGuestPaymentStatus';

/** Premium-gated surfaces in the review page. */
export type PremiumFeature = 'edit' | 'download' | 'letterhead' | 'signature-with-edits';

/**
 * Adapts the existing Blocks paywall (useGuestPaymentStatus + auth) to the
 * per-feature gating API the ported UI expects.
 *
 *  - Authenticated org users and paid guests are always unlocked.
 *  - Only an unauthenticated guest with an active session and an unpaid draft
 *    is gated; requesting a gated feature opens the existing PaymentDialog and
 *    records which feature triggered it (so we can auto-resume after payment).
 */
export function useReviewPremium(draftId: string | null) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const guestSessionId = useGuestSessionStore((s) => s.guestSessionId);
  const paymentStatus = useGuestPaymentStatus(draftId);

  const isGuestUnpaid = !isAuthenticated && !!guestSessionId && !paymentStatus.isPaid;
  const isPremiumUnlocked = !isGuestUnpaid;

  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [triggeredFeature, setTriggeredFeature] = useState<PremiumFeature | null>(null);

  /**
   * Returns true when the feature is allowed to proceed. When gated, opens the
   * paywall and returns false so the caller can bail out.
   */
  const requestPremiumAccess = useCallback(
    (feature: PremiumFeature): boolean => {
      if (isPremiumUnlocked) return true;
      setTriggeredFeature(feature);
      setShowPaymentDialog(true);
      return false;
    },
    [isPremiumUnlocked]
  );

  const dismissPaymentDialog = useCallback(() => setShowPaymentDialog(false), []);

  const onPaid = useCallback(() => {
    paymentStatus.refresh();
    setShowPaymentDialog(false);
  }, [paymentStatus]);

  return {
    isAuthenticated,
    isGuestUnpaid,
    isPremiumUnlocked,
    showPaymentDialog,
    setShowPaymentDialog,
    triggeredFeature,
    requestPremiumAccess,
    dismissPaymentDialog,
    onPaid,
    paymentStatus,
  };
}
